/**
 * La postulación de demostración: la que el candidato se manda A SÍ MISMO.
 *
 * Es el paso donde la compra se vuelve visible. En vez de prometerle que
 * "postulamos por ti", le llega a su bandeja el correo exacto que recibiría un
 * empleador —mismo asunto, mismo mensaje, misma hoja de vida en PDF— saliendo de
 * su propia dirección de envío. Después de verlo tres veces, el muro de pago no
 * pide fe: pide que siga pasando, pero hacia afuera.
 *
 * La cuota es INVISIBLE por diseño. El backend cuenta tres y responde
 * `paywall: true` al completar la tercera; acá NO se dibuja ningún "2 de 3" ni
 * barra de progreso. Un contador convierte un descubrimiento en un trámite, y
 * era además lo que se veía roto como "6/3" cuando el backend generaba de más.
 */
import { apiFetch } from '~/lib/api';
import { messages } from '~/i18n';

/**
 * Cuántas demostraciones da el backend antes del muro. Acá NO se dibuja nunca:
 * sirve para saber, al recargar la página, que el muro ya se había descubierto.
 */
export const DEMO_LIMIT = 3;

export interface DraftApplication {
  _id: string;
  email_subject: string;
  email_body: string;
  match_reason?: string;
  status: string;
  created_at: string;
  /** Marcada por el backend la primera vez que esta postulación llegó al correo. */
  demo_previewed_at?: string | null;
  job?: DraftJob;
}

/**
 * La vacante tal como la manda el panel (la fila de `jobApplyPostings`). Las de
 * Canadá (Job Bank) traen `country: 'CA'` y `wage_currency: 'CAD'`; las del DOL
 * no traen ninguna de las dos, y ausencia = EE. UU. en dólares. La ubicación de
 * Canadá ya viene con el país al final («Smiths Falls, ON, Canada»).
 */
export interface DraftJob {
  title: string;
  company: string;
  location?: string;
  wage_from?: number;
  wage_to?: number;
  wage_per?: string;
  wage_currency?: string;
  country?: string;
}

/**
 * El sueldo de la vacante en una línea: «$18-$20/Hour» para EE. UU. (igual que
 * siempre) y «CAD $18-$20/hour» cuando la vacante dice en qué moneda paga y no
 * es dólar estadounidense. Sin moneda no se adivina: un sueldo canadiense leído
 * como USD es justo el error que esto evita, pero inventar la moneda sería otro.
 */
export function formatJobWage(job?: DraftJob | null): string | null {
  if (!job?.wage_from) return null;
  const range =
    `$${job.wage_from}` +
    (job.wage_to && job.wage_to !== job.wage_from ? `-$${job.wage_to}` : '') +
    `/${job.wage_per || 'Hour'}`;
  const currency = String(job.wage_currency ?? '').trim().toUpperCase();
  return currency && currency !== 'USD' ? `${currency} ${range}` : range;
}

export type DemoSendResult =
  /** Salió. `paywall` viene en true al completar la tercera. */
  | { kind: 'sent'; paywall: boolean; plan_active: boolean }
  /** Cuota agotada y sin plan: el muro, no un error. */
  | { kind: 'plan_required' }
  /** El proveedor de correo está caído. No se envió nada y no se gastó nada. */
  | { kind: 'unavailable' }
  | { kind: 'throttled'; retry_after_seconds: number }
  | { kind: 'failed' };

export async function sendDemo(applicationId: string): Promise<DemoSendResult> {
  let res: Response;
  try {
    res = await apiFetch(`/job-apply/api/applications/${applicationId}/demo-send`, {
      method: 'POST',
    });
  } catch {
    return { kind: 'failed' };
  }

  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;

  if (res.ok) {
    return {
      kind: 'sent',
      paywall: body.paywall === true,
      plan_active: body.plan_active === true,
    };
  }
  // El código del cuerpo manda sobre el status: es el contrato que el backend
  // promete, y sobrevive a un proxy que reescriba el código HTTP.
  if (res.status === 402 || body.code === 'plan_required') return { kind: 'plan_required' };
  if (res.status === 503 || body.code === 'sending_unavailable') return { kind: 'unavailable' };
  if (res.status === 429) {
    const wait = Number(body.retry_after_seconds);
    return { kind: 'throttled', retry_after_seconds: Number.isFinite(wait) && wait > 0 ? wait : 60 };
  }
  return { kind: 'failed' };
}

/**
 * Qué se le dice cuando no salió. Nunca "error 503": lo que necesita saber es si
 * perdió algo (no) y si puede volver a intentar (sí).
 */
export function demoErrorCopy(result: DemoSendResult): string {
  // En el idioma activo al llamarse, nunca al cargar el módulo.
  const copy = messages().workspace.dashboard.demoErrors;
  switch (result.kind) {
    case 'unavailable':
      return copy.unavailable;
    case 'throttled': {
      const minutes = Math.max(1, Math.ceil(result.retry_after_seconds / 60));
      return copy.throttled(minutes);
    }
    default:
      return copy.failed;
  }
}
